/**
 * Meeting detail dependencies provider for the features/meetings flow.
 * Supplies MeetingDetailDependenciesContext so meeting detail components
 * can use API hooks and utilities. Tests and meeting_detail_right_rail
 * reference this module; realtime flow uses RealtimeMeetingDetailDependenciesProvider.
 *
 * Today's meetings for badge logic come from RTK Query: useSearchData publishes
 * todayQueryArgs to Redux; useEventDayMeetings reads them and calls useGetMeetingsForMeetingDetail
 * with the same args for a cache hit.
 */
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { MeetingDetailDependenciesContext, NotetakerProvider, transformLocationKind } from 'components/meetings'
import { RightRailPaywall } from 'components/meetings/meeting_detail/components'
import { MeetingStatusIndicator } from 'components/meetings/MeetingStatusIndicator/MeetingStatusIndicator'
import { getMeetingStatus, getUpNextMeetingId } from 'components/meetings/utils/meeting-helpers'
import { useMeetingsRealtimeFeaturesEnabled } from 'features/meetings/hooks'
import { todayQueryArgsSelector } from 'features/meetings/meetings_page_slice'
import { useScope } from 'hooks/meetings/use_scope'

import {
  useGetInviteesQuery,
  useGetTimelineDataLegacyQuery,
  useGetPollResultsQuery,
  useUpdateMeetingMutation,
  useCancelInviteeMutation,
  useUpdateInviteeMutation,
  useMarkInviteeAsNoShowMutation,
  useUndoNoShowForInviteeMutation,
  useUpdateEventAttributesMutation,
  useBookPollMutation,
  useCancelMeetingMutation,
  useDeletePendingEventMutation
} from '../api'
import { isPendingEvent, mapNodeToMeeting } from '../utils'
import { loadMeetingsModule } from './meeting_detail_analytics_loader'
import { useGetMeetingsForMeetingDetail } from './use_get_meetings_for_meeting_detail'
import { Periods, Statuses } from '../../../constants/meetings'

// Fallbacks when components/meetings does not provide these (e.g. under test mocks)
const emptyObject = () => ({})
const identity = (x) => x

function getAnalyticsOrNoop(name) {
  try {
    const m = loadMeetingsModule()
    const fn = m?.default?.[name]
    if (fn) return fn
    if (name === 'transformLocationKind') return identity
    return emptyObject
  } catch {
    return name === 'transformLocationKind' ? identity : emptyObject
  }
}

export const MeetingDetailDependenciesProvider = ({ children }) => {
  const { scopeType, scopeId } = useScope()
  const todayQueryArgs = useSelector(todayQueryArgsSelector)
  const hasRealtimeFeatures = useMeetingsRealtimeFeaturesEnabled()

  const dependencies = useMemo(() => ({
    ...(hasRealtimeFeatures && {
      StatusIndicator: MeetingStatusIndicator,
      getMeetingStatus,
      getUpNextMeetingId,
    }),
    useGetMeetingsQuery: useGetMeetingsForMeetingDetail,
    useGetInviteesQuery,
    useGetTimelineDataQuery: useGetTimelineDataLegacyQuery,
    useGetPollResultsQuery,
    useUpdateMeetingMutation,
    useCancelInviteeMutation,
    useUpdateInviteeMutation,
    useMarkInviteeAsNoShowMutation,
    useUndoNoShowForInviteeMutation,
    useUpdateEventAttributesMutation,
    useBookPollMutation,
    useCancelMeetingMutation,
    useDeletePendingEventMutation,
    isPendingEvent,
    mapNodeToMeeting,
    RightRailPaywall,
    NotetakerProvider,
    Periods,
    Statuses,
    getScheduleFollowUpAnalyticsPayload: getAnalyticsOrNoop('getScheduleFollowUpAnalyticsPayload'),
    getScheduleInRealTimeAnalyticsPayload: getAnalyticsOrNoop('getScheduleInRealTimeAnalyticsPayload'),
    getReportEventAnalyticsPayload: getAnalyticsOrNoop('getReportEventAnalyticsPayload'),
    getViewMoreInviteesAnalyticsPayload: getAnalyticsOrNoop('getViewMoreInviteesAnalyticsPayload'),
    transformLocationKind,
    todayQueryArgs,
    scope: { scopeType, scopeId },
  }), [hasRealtimeFeatures, todayQueryArgs, scopeType, scopeId])

  return (
    <MeetingDetailDependenciesContext.Provider value={dependencies}>
      {children}
    </MeetingDetailDependenciesContext.Provider>
  )
}
